import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import "./services-responsive.css";
import { getSegmentPath, isSeparateServiceSegment, requiresSegmentAddress } from "../utils/segmentFlow";
import { openDeliveryLocation } from "../utils/locationDefaults";
import ProductImage from "./common/ProductImage";

const DEFAULT_SERVICE_IMAGE = `${process.env.PUBLIC_URL || ""}/assets/images/service-placeholder.png`;

const getServiceId = (item) => item?.segment_id || item?.segmentId || item?.id;

const getServiceName = (item) => {
    const name = item?.name || item?.title || item?.segment_name || "Service";

    return String(name).trim().toLowerCase() === "saloon" ? "Salon" : name;
};

const getServiceItems = (services) => {
    const candidates = [
        services?.cell_contents,
        services?.response_data?.[0]?.cell_contents,
        services?.response_data,
        services?.data?.cell_contents,
        services?.data?.response_data?.[0]?.cell_contents,
        services?.data?.response_data,
        services?.data,
        services,
    ];

    return candidates.find((item) => Array.isArray(item)) || [];
};

const isComingSoonService = (item) =>
    !requiresSegmentAddress(item) && (isSeparateServiceSegment(item) ||
    item?.is_coming_soon === true ||
    Number(item?.is_coming_soon || item?.isComingSoon || item?.coming_soon) === 1);

const ComingSoonBadge = () => (
    <span className="feature-coming-soon-badge">Coming Soon</span>
);

const Services = ({
    services,
    loading = false,
    selectedServiceId = "",
    onServiceSelect,
}) => {
    const navigate = useNavigate();
    const serviceItems = getServiceItems(services);
    const [comingSoonService, setComingSoonService] = useState(null);

    // if (loading) {
    //     return <ServicesSkeleton />;
    // }

    const closeComingSoon = () => {
        setComingSoonService(null);
    };

    useEffect(() => {
        if (!comingSoonService) return undefined;

        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setComingSoonService(null);
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [comingSoonService]);

    if (!serviceItems.length) {
        return null;
    }

    const handleServiceClick = (event, item) => {
        if (requiresSegmentAddress(item)) {
            event.preventDefault();
            openDeliveryLocation({ onSelect: () => {
                if (onServiceSelect) onServiceSelect(item);
                else navigate(getSegmentPath(item), { state: { segment: item } });
            } });
            return;
        }

        if (isComingSoonService(item)) {
            event.preventDefault();
            setComingSoonService(item);
            return;
        }

        if (!onServiceSelect) return;

        event.preventDefault();
        onServiceSelect(item);
    };

    function SampleNextArrow(props) {
        const { className, onClick } = props;
        return (
            <button
                type="button" onClick={onClick}
                className={` ${className} slick-next slick-arrow flex-center rounded-circle bg-white text-xl hover-bg-main-600 hover-text-white transition-1`}
            >
                <i className="ph ph-caret-right" />
            </button>
        );
    }
    function SamplePrevArrow(props) {
        const { className, onClick } = props;

        return (

            <button
                type="button"
                onClick={onClick}
                className={`${className} slick-prev slick-arrow flex-center rounded-circle bg-white text-xl hover-bg-main-600 hover-text-white transition-1`}
            >
                <i className="ph ph-caret-left" />
            </button>
        );
    }
    const settings = {
        dots: false,
        arrows: true,
        infinite: serviceItems.length > 10,
        speed: 1000,
        slidesToShow: 10,
        slidesToScroll: 1,
        initialSlide: 0,
        centerMode: false,   // ensure ye false ho
        nextArrow: <SampleNextArrow />,
        prevArrow: <SamplePrevArrow />,
        responsive: [
            {
                breakpoint: 1699,
                settings: {
                    slidesToShow: 9,
                },
            },
            {
                breakpoint: 1599,
                settings: {
                    slidesToShow: 8,
                },
            },
            {
                breakpoint: 1399,
                settings: {
                    slidesToShow: 6,
                },
            },
            {
                breakpoint: 992,
                settings: {
                    slidesToShow: 5,
                },
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 4,
                },
            },
            {
                breakpoint: 575,
                settings: {
                    slidesToShow: 3,
                },
            },
            {
                breakpoint: 424,
                settings: {
                    slidesToShow: 2,
                },
            },
            {
                breakpoint: 359,
                settings: {
                    slidesToShow: 1,
                },
            },

        ],
    };
    return (
        <div className="feature" id="featureSection">
            <div className="container container-lg">
                <h5 className="mb-10 gap-8">{services?.cell_title_text}</h5>
                <div className="feature-mobile-services" role="list">
                    {serviceItems.map((item, index) => {
                        const serviceId = getServiceId(item);
                        const serviceUrl = getSegmentPath(item);
                        const isSelected = String(serviceId || "") === String(selectedServiceId || "");
                        const serviceName = getServiceName(item);
                        const isComingSoon = isComingSoonService(item);

                        return (
                            <Link
                                to={serviceUrl}
                                state={{ segment: item }}
                                className={`feature-mobile-service ${isSelected ? "is-selected" : ""} ${isComingSoon ? "is-coming-soon" : ""}`}
                                key={`mobile-service-${serviceId || index}`}
                                role="listitem"
                                onClick={(event) => handleServiceClick(event, item)}
                            >
                                <span className="feature-mobile-service__image">
                                    {isComingSoon && <ComingSoonBadge />}
                                    <ProductImage src={item.image || item.segment_icon} fallbackSrc={DEFAULT_SERVICE_IMAGE} alt={serviceName} />
                                </span>
                                <span className="feature-mobile-service__name">
                                    <span className="feature-service-name__text">{serviceName}</span>
                                    {isSelected && (
                                        <span className="feature-selected-name-icon" aria-label="Selected">
                                            <i className="ph ph-check" aria-hidden="true" />
                                        </span>
                                    )}
                                </span>
                            </Link>
                        );
                    })}
                </div>
                <div className="position-relative arrow-center feature-desktop-slider">
                    <div className="flex-align">
                        <button
                            type="button"
                            id="feature-item-wrapper-prev"
                            className="slick-prev slick-arrow flex-center rounded-circle bg-white text-xl hover-bg-main-600 hover-text-white transition-1"
                        >
                            <i className="ph ph-caret-left" />
                        </button>
                        <button
                            type="button"
                            id="feature-item-wrapper-next"
                            className="slick-next slick-arrow flex-center rounded-circle bg-white text-xl hover-bg-main-600 hover-text-white transition-1"
                        >
                            <i className="ph ph-caret-right" />
                        </button>
                    </div>
                    <div className="feature-item-wrapper">
                        <Slider {...settings}>
                        {serviceItems.map((item, index) => {
                            const serviceId = getServiceId(item);
                            const isSelected = String(serviceId || "") === String(selectedServiceId || "");
                            const serviceName = getServiceName(item);
                            const isComingSoon = isComingSoonService(item);

                            return (

                              <div className={`feature-item text-center ${isSelected ? "is-selected" : ""} ${isComingSoon ? "is-coming-soon" : ""}`} key={serviceId || index}>

                                <div className="feature-item__thumb rounded-circle">
                                  <Link
                                    to={getSegmentPath(item)}
                                    state={{ segment: item }}
                                    className="w-100 h-100 flex-center"
                                    onClick={(event) => handleServiceClick(event, item)}
                                  >
                                    {isComingSoon && <ComingSoonBadge />}
                                    <ProductImage src={item.image || item.segment_icon} fallbackSrc={DEFAULT_SERVICE_IMAGE} alt={serviceName} className="feature-item__image" />
                                  </Link>
                                </div>

                                <div className="feature-item__content mt-16">
                                  <h6 className="text-lg mb-8">
                                    <Link
                                      to={getSegmentPath(item)}
                                      state={{ segment: item }}
                                      className="text-inherit"
                                      onClick={(event) => handleServiceClick(event, item)}
                                    >
                                      <span className="feature-service-name">
                                        <span className="feature-service-name__text">{serviceName}</span>
                                        {isSelected && (
                                          <span className="feature-selected-name-icon" aria-label="Selected">
                                            <i className="ph ph-check" aria-hidden="true" />
                                          </span>
                                        )}
                                      </span>
                                    </Link>
                                  </h6>

                                  {/* Optional */}
                                  {/* <span className="text-sm text-gray-400">{item.product_count} Products</span> */}

                                </div>

                              </div>

                            );
                          })}
                        </Slider>
                    </div>
                </div>
            </div>
            {comingSoonService && (
                <div
                    className="service-coming-soon-overlay"
                    role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) {
                            closeComingSoon();
                        }
                    }}
                >
                    <div
                        className="service-coming-soon-panel"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="service-coming-soon-title"
                    >
                        <button
                            type="button"
                            className="service-coming-soon-close"
                            onClick={closeComingSoon}
                            aria-label="Close"
                        >
                            <i className="ph ph-x" aria-hidden="true" />
                        </button>
                        <span className="service-coming-soon-handle" aria-hidden="true" />
                        <div className="service-coming-soon-image">
                            <ProductImage src={comingSoonService.image || comingSoonService.segment_icon} fallbackSrc={DEFAULT_SERVICE_IMAGE} alt={getServiceName(comingSoonService)} />
                        </div>
                        <h3 id="service-coming-soon-title">
                            {getServiceName(comingSoonService)} is coming soon!
                        </h3>
                        <p>We're working hard to bring this service to your area. Stay tuned!</p>
                        <button
                            type="button"
                            className="service-coming-soon-primary"
                            onClick={closeComingSoon}
                        >
                            <i className="ph ph-bell-ringing" aria-hidden="true" />
                            <span>Notify me - It's on</span>
                        </button>
                        <button
                            type="button"
                            className="service-coming-soon-secondary"
                            onClick={closeComingSoon}
                        >
                            Explore available services
                        </button>
                    </div>
                </div>
            )}
        </div>

    )
}

export default Services
